import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  styled,
  useTheme,
  alpha
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  PhotoLibrary as PhotoIcon
} from '@mui/icons-material';
import { useDesigner } from '../../contexts/DesignerContext';
import { uploadGarment } from '../../api/files';

// Hidden file input
const HiddenInput = styled('input')({
  display: 'none',
});

interface SourceImageUploaderProps {
  username: string;
  projectName: string;
  onImageUpload?: (imagePath: string) => void;
}

const SourceImageUploader: React.FC<SourceImageUploaderProps> = ({
  username,
  projectName,
  onImageUpload
}) => {
  const { setSourceImage } = useDesigner();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    handleFileUpload(file);
  };

  const handleFileUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setUploadError('Proszę wybrać plik obrazu (JPG, PNG, AVIF, WEBP)');
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(false);

    try {
      // Create a preview URL
      const previewUrl = URL.createObjectURL(file);
      setUploadedImage(previewUrl);

      // Upload the file
      const result = await uploadGarment(file, username, projectName);

      if (result.status === 'success' && result.path) {
        setUploadSuccess(true);
        setIsUploading(false);
        
        // Clean up blob URL to avoid memory leaks
        if (previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(previewUrl);
        }
        
        // Update preview to show server path instead of blob URL
        setUploadedImage(result.path);
        
        // Update the source image in the context
        setSourceImage(result.path);
        
        // Notify parent component if callback provided
        if (onImageUpload) {
          onImageUpload(result.path);
        }
      } else {
        setUploadError(result.message || 'Błąd podczas przesyłania pliku');
        setIsUploading(false);
      }
    } catch (error) {
      console.error('Error uploading source image:', error);
      setUploadError(error instanceof Error ? error.message : 'Nieznany błąd podczas przesyłania');
      setIsUploading(false);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      handleFileUpload(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const clearUploadedImage = () => {
    // Clean up blob URL if it exists
    if (uploadedImage && uploadedImage.startsWith('blob:')) {
      URL.revokeObjectURL(uploadedImage);
    }
    
    setUploadedImage(null);
    setUploadSuccess(false);
    setUploadError(null);
    setSourceImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 0, mb: 4, borderRadius: 3, overflow: 'hidden', boxShadow: '0 6px 18px rgba(0, 0, 0, 0.08)' }}>
      {/* Header Section */}
      <Box sx={{ 
        p: 3, 
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)', 
        backgroundColor: 'rgba(25, 118, 210, 0.03)', 
        display: 'flex', 
        alignItems: 'center' 
      }}>
        <PhotoIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
        <Typography variant="h5" fontWeight={600} sx={{ color: 'primary.main', letterSpacing: '-0.01em' }}>
          Zdjęcie produktu
        </Typography>
      </Box>
      
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          Wybierz zdjęcie z odzieżą lub ubranej osoby.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <strong>Obsługiwane formaty:</strong> JPG, PNG, AVIF, WEBP
        </Typography>
        
        {/* Upload Area */}
        <Box
          sx={{
            p: 4,
            border: '2px dashed',
            borderColor: alpha('#1976d2', 0.3),
            bgcolor: alpha('#1976d2', 0.02),
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            minHeight: 200,
            position: 'relative',
            '&:hover': {
              borderColor: '#1976d2',
              bgcolor: alpha('#1976d2', 0.05)
            }
          }}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          {uploadedImage ? (
            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
              <img 
                src={uploadedImage} 
                alt="Przesłane zdjęcie modela" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: 400,
                  objectFit: 'contain',
                  borderRadius: 8
                }}
              />
              <IconButton
                size="small"
                sx={{ 
                  position: 'absolute',
                  top: -16,
                  right: -16,
                  bgcolor: 'error.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'error.dark'
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  clearUploadedImage();
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <UploadIcon sx={{ fontSize: 60, color: '#1976d2', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Przeciągnij i upuść zdjęcie produktu tutaj
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  lub
                </Typography>
                <Button variant="contained" sx={{ mt: 2 }}>
                  Wybierz plik
                </Button>
              </Box>
            </>
          )}
          
          <HiddenInput
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleFileChange}
          />
          
          {/* Upload status */}
          {isUploading && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              <Typography>Przesyłanie...</Typography>
            </Box>
          )}
        </Box>
        
        {uploadError && (
          <Alert 
            severity="error"
            sx={{ mt: 2 }}
            action={
              <IconButton
                color="inherit"
                size="small"
                onClick={() => setUploadError(null)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            {uploadError}
          </Alert>
        )}
        
        {uploadSuccess && (
          <Alert 
            severity="success"
            sx={{ mt: 2 }}
          >
            Zdjęcie zostało pomyślnie przesłane.
          </Alert>
        )}
        
        {/* Best practices and tips */}
        <Paper 
          elevation={0} 
          sx={{ 
            mt: 3, 
            p: 2, 
            bgcolor: alpha(theme.palette.info.main, 0.05),
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            borderRadius: 2
          }}
        >
          <Typography variant="subtitle2" color="info.main" gutterBottom>
            Wskazówki dla najlepszych rezultatów:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <Box component="li">
              <Typography variant="body2">Wybierz zdjęcie z dobrym oświetleniem i jednolitym tłem</Typography>
            </Box>
            <Box component="li">
              <Typography variant="body2">Osoba powinna być widoczna w całości lub wyraźnie pokazywać odzież do przeniesienia</Typography>
            </Box>
            <Box component="li">
              <Typography variant="body2">Unikaj skomplikowanych wzorów i nakładających się elementów</Typography>
            </Box>
            <Box component="li">
              <Typography variant="body2">Im wyższa jakość zdjęcia, tym lepszy efekt końcowy</Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Paper>
  );
};

export default SourceImageUploader;